import { AfterViewInit, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormControlDirective, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { componentContainerAnimation, labelAnimation, tickAnimation } from '../animations';
import { Abstract_Form_Component, FormComponent } from '../form-component.types';
import { DataService } from '../form-core/data/data.service';
import { OptionSetting } from '../options/options.component';
import { ScrollService } from '../step/scroll.service';

@Component({
  selector: 'div[data-component-type=select]',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [labelAnimation, tickAnimation, componentContainerAnimation]
})
export class SelectComponent extends Abstract_Form_Component implements OnInit, OnDestroy, AfterViewInit {

  @HostListener('focusout') focusout() {
    this.focused = false
    if (!this.required && !this.controlValueIsNotFalsy) {
      this.formControl.reset()
      this.touchedOrDirty = false
      this.touchedOrDirtyAndInvalid = false
      this.labelState = 'hideLabel'
    } else {
      this.touchedOrDirty = true
      this.touchedOrDirtyAndInvalid = this.formControl.invalid
      this.labelState = 'displayLabel'
    }
  }
  @HostListener('focusin') focusin() {
    this.focused = true
    if (this.isMobile && !this.controlValueIsNotFalsy && this.isiOS) this.formControl.setValue(this.options[0].value)
  }
  @HostListener('mousedown', ['$event']) mousedown(event: MouseEvent) {
    if (!this.isMobile && event.target !== this.div.nativeElement && !this.focused) {
      event.stopPropagation()
      event.preventDefault()
      this.div.nativeElement.focus()
    }
  }

  @Input() options!: OptionSetting[]

  @ViewChild('div') div!: ElementRef<HTMLDivElement>
  @ViewChild(FormControlDirective) controlDirective!: FormControlDirective;

  textValue!: string
  isMobile: boolean

  private destroy$: Subject<any> = new Subject()
  private isiOS: boolean

  constructor(scroll: ScrollService, cd: ChangeDetectorRef, data: DataService, private el: ElementRef<HTMLElement>) {
    super(cd, scroll);
    this.isMobile = data.device.device?.type !== 'desktop'
    this.isiOS = data.device.os?.name === 'iOS'
  }

  ngOnInit(): void {
    this.formControl.statusChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.error = this.formControl.invalid ? Object.keys(this.formControl.errors as ValidationErrors)[0] : null
      this.touchedOrDirtyAndInvalid = this.touchedOrDirty && this.formControl.invalid
    })
    this.formControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.updateControlValueFalsy()
      this.touchedOrDirty = this.formControl.touched || this.formControl.dirty
      this.updateLabelState()
      if (this.isMobile) this.textValue = this.options.find(option => option.value === value)?.text as string
    })
    this.scroll.notice$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(path => {
      if (this.formControl.invalid) {
        this.formControl.pristine && this.formControl.markAsDirty()
        this.formControl.untouched && this.formControl.markAsTouched()
        this.touchedOrDirty = true
        this.touchedOrDirtyAndInvalid = true
        this.labelState = 'displayLabel'
      }
      if (path === this.path) this.el.nativeElement.scrollIntoView()
      this.cd.markForCheck()
    })
    this.updateControlValueFalsy()
    this.error = this.formControl.invalid ? Object.keys(this.formControl.errors as ValidationErrors)[0] : null
    this.touchedOrDirty = this.formControl.touched || this.formControl.dirty
    this.touchedOrDirtyAndInvalid = this.formControl.invalid && this.touchedOrDirty
    if (this.controlValueIsNotFalsy) {
      this.textValue = this.options.find(option => option.value === this.formControl.value)?.text as string
    }
    this.updateLabelState()
  }

  ngAfterViewInit(): void {
    this.scroll.onInit$.pipe(
      first()
    ).subscribe(path => {
      if (path === this.path) this.el.nativeElement.scrollIntoView()
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next('')
    this.destroy$.complete()
    //@ts-ignore
    this.controlDirective?._invokeOnDestroyCallbacks()
  }

  trackByFn(index: number, item: OptionSetting) {
    return item.text
  }

  updateLabelState(): void {
    this.labelState = this.touchedOrDirty || this.controlValueIsNotFalsy ? 'displayLabel' : 'hideLabel'
  }

  updateValue(event: { text: string, value: any }) {
    this.formControl.setValue(event.value)
    this.formControl.markAsDirty()
    this.textValue = event.text
  }

}

export interface SelectOptions extends FormComponent {
  id: string,
  type: 'select-component',
  required: boolean,
  formControl: FormControl,
  presence: boolean,
  options: OptionSetting[]
}
